import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/common/Combobox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, Wallet, Plus, IndianRupee } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const PAY_MODES = ["BANK_TRANSFER", "NEFT", "RTGS", "IMPS", "UPI", "CHEQUE", "CASH", "OTHER"];
const STATUS_COLOR = { PAID: "text-emerald-700", PARTIAL: "text-amber-700", UNPAID: "text-rose-600" };

export default function SupplierStatement() {
  const { data: supRaw } = useResource("/suppliers");
  const suppliers = Array.isArray(supRaw) ? supRaw : [];
  const [tab, setTab] = useState("statement");
  const [supplierId, setSupplierId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [applied, setApplied] = useState({});

  const supOptions = suppliers.map((s) => ({ value: s.supplierId || s.id, label: s.name, hint: s.supplierId }));
  const selectedSup = suppliers.find((s) => (s.supplierId || s.id) === supplierId);

  const qs = useMemo(() => {
    if (!applied.supplierId) return "";
    const p = new URLSearchParams({ supplierId: applied.supplierId });
    if (applied.dateFrom) p.set("dateFrom", applied.dateFrom);
    if (applied.dateTo) p.set("dateTo", applied.dateTo);
    if (applied.paymentStatus) p.set("paymentStatus", applied.paymentStatus);
    return `?${p.toString()}`;
  }, [applied]);

  const { data, refetch } = useResource(qs ? `/supplier-statement${qs}` : null);
  const L = data || {};
  const rows = L.rows || [];
  const t = L.totals || {};

  const run = () => {
    if (!supplierId) return toast.error("Select a supplier / store first.");
    setApplied({ supplierId, dateFrom, dateTo, paymentStatus });
  };

  const doExport = async (fmt) => {
    if (!qs) return toast.error("Run the statement first.");
    try {
      const res = await api.get(`/supplier-statement/export${qs}&format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `supplier-statement.${fmt === "excel" ? "xlsx" : fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  // ---------- payment dialog ----------
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({});
  const [allocs, setAllocs] = useState({}); // billId -> amount
  const { data: openInvRaw, refetch: refetchOpen } = useResource(payOpen && supplierId ? `/suppliers/${supplierId}/open-invoices` : null);
  const openInv = (Array.isArray(openInvRaw) ? openInvRaw : []).filter((o) => o.balance > 0.009);
  const [busy, setBusy] = useState(false);

  const openPay = () => {
    if (!supplierId) return toast.error("Select a supplier / store first.");
    setPay({ amount: "", paymentMode: "BANK_TRANSFER", paymentDate: new Date().toISOString().slice(0, 10), bankAccount: "", reference: "", narration: "" });
    setAllocs({});
    setPayOpen(true);
  };

  const allocTotal = Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0);
  const onAccount = Math.round(((Number(pay.amount) || 0) - allocTotal) * 100) / 100;

  const savePayment = async () => {
    const amt = Number(pay.amount);
    if (!amt || amt <= 0) return toast.error("Enter a payment amount.");
    if (allocTotal > amt + 0.001) return toast.error("Allocated amount exceeds the payment amount.");
    setBusy(true);
    try {
      const res = await api.post("/supplier-payments", { supplierId, ...pay, amount: amt });
      const pid = res.data.id;
      const allocations = Object.entries(allocs)
        .filter(([, v]) => Number(v) > 0)
        .map(([billId, v]) => ({ billId, amount: Number(v) }));
      if (allocations.length) await api.put(`/supplier-payments/${pid}/allocate`, { allocations });
      await api.post(`/supplier-payments/${pid}/post`, {});
      toast.success(`Payment ${res.data.paymentNo} recorded${onAccount > 0 ? ` (₹${onAccount} on account)` : ""}.`);
      setPayOpen(false);
      refetch(); refetchOpen();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not save payment")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Supplier Statement & Payments" subtitle="Purchase invoices, payments and outstanding — per supplier / store" icon={Wallet} />

      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <div className="sm:col-span-2">
            <Label>Supplier / Store</Label>
            <Combobox testId="ss-supplier" value={supplierId} onChange={setSupplierId} placeholder="Select supplier / store…" options={supOptions} />
          </div>
          <div><Label>Date From</Label><Input data-testid="ss-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
          <div><Label>Date To</Label><Input data-testid="ss-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          <div>
            <Label>Payment Status</Label>
            <Select value={paymentStatus || "ALL"} onValueChange={(v) => setPaymentStatus(v === "ALL" ? "" : v)}>
              <SelectTrigger data-testid="ss-paystatus"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="UNPAID">Unpaid</SelectItem>
                <SelectItem value="PARTIAL">Partially Paid</SelectItem>
                <SelectItem value="PAID">Fully Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button data-testid="ss-run" className="bg-amber-700 hover:bg-amber-800" onClick={run}>View Statement</Button>
          <Button data-testid="ss-add-payment" variant="outline" className="gap-1.5" onClick={openPay}><Plus className="h-4 w-4" /> Record Payment</Button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => doExport("pdf")} data-testid="ss-pdf"><Download className="h-4 w-4" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => doExport("csv")} data-testid="ss-csv">CSV</Button>
            <Button variant="outline" size="sm" onClick={() => doExport("excel")} data-testid="ss-excel">Excel</Button>
          </div>
        </div>
      </div>

      {L.supplier && (
        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-100 p-4">
            <div className="text-lg font-semibold text-stone-800">{L.supplier.name}</div>
            <div className="text-xs text-stone-500">{L.supplier.supplierId} · GSTIN {L.supplier.gstin || "—"} · {L.supplier.city || ""}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5">
            {[["Opening", t.openingBalance], ["Purchases", t.purchases], ["Payments", t.payments], ["Debit Notes", t.debitNotes], ["Outstanding", t.closing]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-stone-50 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">{k}</div>
                <div className={`text-sm font-semibold tabular-nums ${k === "Outstanding" ? "text-rose-700" : "text-stone-800"}`}>{money(v)}</div>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-y border-stone-200 bg-stone-50 text-left text-[10px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-3 py-2">Date</th><th className="px-3 py-2">Invoice / Ref</th><th className="px-3 py-2">Particulars</th>
                  <th className="px-3 py-2 text-right">Purchase (Cr)</th><th className="px-3 py-2 text-right">Payment (Dr)</th>
                  <th className="px-3 py-2 text-right">Outstanding</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-testid="ss-rows">
                <tr className="bg-amber-50/40"><td className="px-3 py-2 text-xs text-stone-500" colSpan={7}>Opening Balance</td><td className="px-3 py-2 text-right font-mono tabular-nums">{money(t.openingBalance)}</td></tr>
                {rows.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-muted-foreground">No transactions in this period.</td></tr> :
                  rows.map((r, i) => (
                    <tr key={i} data-testid={`ss-row-${i}`}>
                      <td className="px-3 py-2 text-stone-600">{r.date}</td>
                      <td className="px-3 py-2 font-medium text-stone-800">{r.docNo}</td>
                      <td className="px-3 py-2 text-stone-600">{r.particulars}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{r.credit ? money(r.credit) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{r.debit ? money(r.debit) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{r.kind === "BILL" ? money(r.outstanding) : "—"}</td>
                      <td className={`px-3 py-2 text-xs font-medium ${STATUS_COLOR[r.paymentStatus] || "text-stone-400"}`}>{r.paymentStatus || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">{money(r.balance)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><IndianRupee className="h-5 w-5 text-amber-700" /> Record Supplier Payment</DialogTitle>
            <DialogDescription className="text-xs">{selectedSup?.name} · select invoices to settle, or leave unallocated for On Account.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Payment Date</Label><Input data-testid="sp-date" type="date" value={pay.paymentDate || ""} onChange={(e) => setPay({ ...pay, paymentDate: e.target.value })} /></div>
            <div><Label>Amount (₹)</Label><Input data-testid="sp-amount" type="number" value={pay.amount || ""} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={pay.paymentMode} onValueChange={(v) => setPay({ ...pay, paymentMode: v })}>
                <SelectTrigger data-testid="sp-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PAY_MODES.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bank / Cash</Label><Input data-testid="sp-bank" value={pay.bankAccount || ""} onChange={(e) => setPay({ ...pay, bankAccount: e.target.value })} /></div>
            <div><Label>Reference / UTR / Cheque</Label><Input data-testid="sp-ref" value={pay.reference || ""} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
            <div><Label>Remarks</Label><Input data-testid="sp-remarks" value={pay.narration || ""} onChange={(e) => setPay({ ...pay, narration: e.target.value })} /></div>
          </div>

          <div className="mt-2 rounded-lg border border-stone-200">
            <div className="border-b border-stone-100 bg-stone-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-stone-500">Outstanding Invoices — tick & enter amount</div>
            {openInv.length === 0 ? <div className="px-3 py-4 text-center text-xs text-muted-foreground">No outstanding invoices for this supplier.</div> :
              <table className="w-full text-sm">
                <tbody className="divide-y divide-stone-100">
                  {openInv.map((o) => {
                    const on = allocs[o.billId] !== undefined;
                    return (
                      <tr key={o.billId} data-testid={`sp-inv-${o.billId}`}>
                        <td className="px-3 py-2 w-8">
                          <Checkbox data-testid={`sp-inv-check-${o.billId}`} checked={on}
                            onCheckedChange={(c) => setAllocs((s) => { const n = { ...s }; if (c) n[o.billId] = o.balance; else delete n[o.billId]; return n; })} />
                        </td>
                        <td className="px-3 py-2 font-medium">{o.invoiceNo}</td>
                        <td className="px-3 py-2 text-stone-500">{o.invoiceDate}</td>
                        <td className="px-3 py-2 text-right font-mono">{money(o.balance)}</td>
                        <td className="px-3 py-2 w-32">
                          {on && <Input data-testid={`sp-inv-amt-${o.billId}`} type="number" className="h-8" value={allocs[o.billId]}
                            onChange={(e) => setAllocs((s) => ({ ...s, [o.billId]: e.target.value }))} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>}
            <div className="flex justify-between border-t border-stone-100 px-3 py-2 text-xs">
              <span>Allocated: <b>{money(allocTotal)}</b></span>
              <span className={onAccount < 0 ? "text-rose-600" : "text-stone-600"}>On Account: <b>{money(onAccount)}</b></span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} data-testid="sp-cancel">Cancel</Button>
            <Button className="bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={savePayment} data-testid="sp-save">Save & Post Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
